import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { SurveyQuestion } from '../../fetchSurvey/types';
import type { LogicContext } from '../types';
import { evaluateDisplayLogic } from '../evaluator/evaluateDisplayLogic';
import {
  hasAnswerLogic,
  hasRowAnswerLogic,
  hasScaleColumnAnswerLogic,
  isMatrixType,
} from './questionAccess';
import {
  getVisibleMcqOptions,
  getVisibleRankOrderOptions,
  getVisibleScaleColumns,
  getVisibleStatementRows,
} from './visibleItems';

export function isQuestionAnswerable(question: SurveyQuestion, context: LogicContext): boolean {
  if (question.type === QUESTION_TYPE.MCQ) {
    return getVisibleMcqOptions(question, context).length > 0;
  }

  if (question.type === QUESTION_TYPE.RANK_ORDER) {
    return getVisibleRankOrderOptions(question, context).length > 0;
  }

  if (isMatrixType(question.type)) {
    const visibleRows = getVisibleStatementRows(question, context);
    if (visibleRows.length === 0) {
      return false;
    }

    if (hasScaleColumnAnswerLogic(question.type)) {
      return getVisibleScaleColumns(question, context).length > 0;
    }

    return true;
  }

  return true;
}

export function isQuestionVisible(question: SurveyQuestion, context: LogicContext): boolean {
  if (question.displayLogic && !evaluateDisplayLogic(question.displayLogic, context)) {
    return false;
  }

  if (hasRowAnswerLogic(question)) {
    if (getVisibleStatementRows(question, context).length === 0) {
      return false;
    }
  }

  if (hasAnswerLogic(question) && !isQuestionAnswerable(question, context)) {
    return false;
  }

  return true;
}

export function getVisibleQuestions(
  questions: SurveyQuestion[],
  context: LogicContext
): SurveyQuestion[] {
  return questions.filter(question => isQuestionVisible(question, context));
}

export function isNonDynamicDisplayQuestion(question: SurveyQuestion): boolean {
  return question.displayLogic?.isDynamicAppearanceEnabled === false;
}
